import { useContext } from "react";
import { AdvancedSearchContext } from "~/providers/AdvancedSearchProvider";
import { Language, LanguageContext, Locale } from "~/providers/LanguageProvider";
import { fetchCompanies, fetchIndustries, fetchDynamicFilters } from "~/services/api/filters";
import { useTabStore, useFiltersStore } from "~/stores";
import { FilterActions } from "~/types/actions.enum";
import { FilterOption, FilterType } from "~/types/filters.types";
import {
    SEARCH_TABS,
    SEARCH_TYPE_BY_TAB,
    SolrFilterParams,
    SolrSearchType
} from "~/types/search.types";
import {
    convertYearDataToFilterObject,
    convertCompanyDataToFilterObject,
    convertIndustryDataToFilterObject
} from "~/utils/filterUtils";

const useFetchFilterData = () => {
    const {
        yearFilters,
        industryFilters,
        imageThemeFilters,
        pageSectionFilters,
        disclosureFilters,
        companyFilters,
        searchTerm
    } = useContext(AdvancedSearchContext);
    const { language } = useContext(LanguageContext);

    // Store methods
    const dispatch = useFiltersStore(state => state.dispatch);
    const getIndustryById = useFiltersStore(state => state.getIndustryById);
    const getCompanyById = useFiltersStore(state => state.getCompanyById);

    const activeTab = useTabStore(state => state.tab);
    const isFullDocumentsTab = activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS;
    const langLocale = language === Language.EN ? Locale.US : Locale.FR;

    const isDefined = <T>(value: T | undefined): value is T => value !== undefined;
    const setFilterOptions = (
        resFilters: string[],
        action:
            | FilterActions.SET_YEAR_FILTERS
            | FilterActions.SET_INDUSTRY_FILTERS
            | FilterActions.SET_COMPANY_FILTERS,
        mapFn: (id: string) => FilterOption | undefined
    ) => {
        const newFilters = resFilters.map(mapFn).filter(isDefined);
        dispatch({ type: action, payload: newFilters });
    };

    const fetchIndustriesData = async () => {
        // API call to fetch all industries data
        // Store to filters store for data reference
        let response = await fetchIndustries();
        const resIndustries = response.data;
        const industryFilters = resIndustries.map(item => convertIndustryDataToFilterObject(item));

        // Store to filters store for data reference
        dispatch({
            type: FilterActions.SET_INDUSTRIES_DATA,
            payload: industryFilters
        });
    };

    const fetchCompaniesData = async () => {
        // API to fetch all companies data
        // Fetch all companies and their data
        let response = await fetchCompanies();
        const resCompanies = response.data;
        const companyFilters = resCompanies.map(item => convertCompanyDataToFilterObject(item));

        // Store to filters store for data reference
        dispatch({
            type: FilterActions.SET_COMPANIES_DATA,
            payload: companyFilters
        });
    };

    const fetchFilterOptions = async (
        filtersToUpdate?: Array<FilterType.YEAR | FilterType.INDUSTRY | FilterType.COMPANY>
    ) => {
        const resetYears =
            isFullDocumentsTab || !filtersToUpdate || filtersToUpdate.includes(FilterType.YEAR);
        const searchYrs = resetYears ? [] : yearFilters.map(year => year.id);
        const searchCompanies = isFullDocumentsTab ? companyFilters.map(company => company.id) : [];
        const payload: SolrFilterParams = {
            DocumentTypeID: disclosureFilters.map(disclosure => Number(disclosure.id)),
            SelectedBookmarkMetadata: pageSectionFilters.map(page => Number(page.id)),
            SelectedMetadata: imageThemeFilters.map(img => Number(img.id)),
            DocumentYear: searchYrs,
            Industries: industryFilters.map(industry => industry.id),
            SearchTerm: searchTerm,
            Companies: searchCompanies,
            LanguageCode: langLocale
        };

        const tabKey = activeTab?.key ?? SEARCH_TABS.IMAGES;
        const searchType = SEARCH_TYPE_BY_TAB[tabKey] as SolrSearchType;

        const shouldUpdate = (type: Exclude<FilterType, FilterType.DISCLOSURE>) =>
            !filtersToUpdate || filtersToUpdate.includes(type);

        try {
            let response = await fetchDynamicFilters(payload, searchType);
            const data = response.data;

            if (!data) {
                return;
            }

            const { Year, IndustryID, CompanyID } = data;

            if (shouldUpdate(FilterType.YEAR)) {
                const reverseYrs = Year.reverse();
                setFilterOptions(reverseYrs, FilterActions.SET_YEAR_FILTERS, yr =>
                    convertYearDataToFilterObject(yr)
                );
                dispatch({ type: FilterActions.SET_IS_LOADING_YEARS, payload: false });
            }

            // No updates required for these filters for the Full Documents
            if (!isFullDocumentsTab) {
                if (shouldUpdate(FilterType.INDUSTRY)) {
                    setFilterOptions(IndustryID, FilterActions.SET_INDUSTRY_FILTERS, id =>
                        getIndustryById(Number(id))
                    );
                    dispatch({ type: FilterActions.SET_IS_LOADING_INDUSTRIES, payload: false });
                }

                if (shouldUpdate(FilterType.COMPANY)) {
                    setFilterOptions(CompanyID, FilterActions.SET_COMPANY_FILTERS, id =>
                        getCompanyById(Number(id))
                    );
                    dispatch({ type: FilterActions.SET_IS_LOADING_COMPANIES, payload: false });
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchYears = async (filters?: {
        DocumentTypeID?: number[];
        CategoryId?: number[];
    }): Promise<number[]> => {
        const payload: SolrFilterParams = {
            DocumentTypeID: filters?.DocumentTypeID || [],
            SelectedBookmarkMetadata: [],
            SelectedMetadata: [],
            DocumentYear: [],
            Industries: [],
            SearchTerm: "",
            Companies: [],
            LanguageCode: langLocale,
            // Add CategoryId if passed
            ...(filters?.CategoryId ? { CategoryId: filters.CategoryId } : {}),
        };

        const tabKey = activeTab?.key ?? SEARCH_TABS.IMAGES;
        const searchType = SEARCH_TYPE_BY_TAB[tabKey] as SolrSearchType;

        try {
            const response = await fetchDynamicFilters(payload, searchType);
            const data = response.data;
            if (!data?.Year) return [];

            return data.Year.map(y => Number(y)).sort((a, b) => b - a); // descending
        } catch (error) {
            console.error("Error fetching years:", error);
            return [];
        }
    };

        return {
            fetchIndustriesData,
            fetchCompaniesData,
            fetchFilterOptions,
            fetchYears
        };
    };

export default useFetchFilterData;
